/**
 * Gamification API — five-feedback-service.
 *
 * Streak, points, achievements, and activity recording.
 * Base path: /api/feedback/api/v1/gamification
 */

const GAMIFICATION_BASE = '/api/feedback/api/v1/gamification';
const TOKEN_STORAGE_KEY = 'five_ai_access_token';

function getBaseUrl(): string {
  if (typeof window === 'undefined') return '';
  return window.location.origin;
}

function getAuthHeader(): Record<string, string> {
  if (typeof window === 'undefined') return {};
  const token = localStorage.getItem(TOKEN_STORAGE_KEY);
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function gamificationFetch<T>(path: string, options: RequestInit = {}): Promise<T> {
  const url = `${getBaseUrl()}${GAMIFICATION_BASE}${path}`;
  const res = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeader(),
      ...(options.headers as Record<string, string> ?? {}),
    },
  });
  if (!res.ok) {
    throw new Error(`Gamification API error: ${res.status}`);
  }
  return res.json() as Promise<T>;
}

// ─── Types ───────────────────────────────────────────────────────────────────

export interface StreakMilestone {
  id: string;
  dayThreshold: number;
  name: string;
  description?: string | null;
  bonusPoints: number;
  streakFreezesAwarded: number;
}

export interface UserStreakResponse {
  id: string;
  userId: string;
  currentStreak: number;
  longestStreak: number;
  lastActiveDate: string | null;
  streakFreezesAvailable: number;
  streakFreezesUsed: number;
  streakActive: boolean;
  totalActiveDays: number;
  currentStreakStartDate: string | null;
  upcomingMilestones: StreakMilestone[];
  claimedMilestones: StreakMilestone[];
}

export interface UserPointsResponse {
  id: string;
  userId: string;
  totalPoints: number;
  currentBalance: number;
  weeklyPoints: number;
  monthlyPoints: number;
  level: number;
  totalTransactions: number;
  pointsToNextLevel: number;
}

export interface GamificationSummary {
  streak: UserStreakResponse;
  points: UserPointsResponse;
  recentAchievements: { id: string; name: string; description?: string; tier?: string; iconUrl?: string; earnedAt?: string }[];
  totalAchievementsEarned: number;
  totalAchievementsAvailable: number;
  leaderboardRank: number;
}

// ─── API ─────────────────────────────────────────────────────────────────────

export const gamificationApi = {
  /** Full gamification summary (streak + points + recent achievements). */
  getSummary(userId: string): Promise<GamificationSummary> {
    return gamificationFetch<GamificationSummary>(`/users/${userId}/summary`);
  },

  /** Get user streak with upcoming milestones. */
  getStreak(userId: string): Promise<UserStreakResponse> {
    return gamificationFetch<UserStreakResponse>(`/users/${userId}/streak`);
  },

  /** Get user points and level. */
  getPoints(userId: string): Promise<UserPointsResponse> {
    return gamificationFetch<UserPointsResponse>(`/users/${userId}/points`);
  },

  /**
   * Record daily activity for the user — updates streak and awards points.
   * Call this when the user engages with the coach or completes an activity.
   * Idempotent per day (safe to call multiple times).
   */
  recordActivity(userId: string): Promise<UserStreakResponse> {
    return gamificationFetch<UserStreakResponse>(`/users/${userId}/record-activity`, {
      method: 'POST',
    });
  },
};
